import { NoLauncherError } from './errors';

import type { RawEnvelope } from './types';

// The launcher decodes the whole file (up to 375 MiB from 500 MiB of base64) before it answers,
// about 9 s on a fast machine, and this only catches a launcher that never answers.
const LAUNCHER_ANSWER_TIMEOUT_MS = 60_000;

const READY_MESSAGE = { type: 'orsnn-irm-ready' } as const;
const ENVELOPE_MESSAGE_TYPE = 'orsnn-irm-envelope';

function asRawEnvelope(data: unknown): RawEnvelope | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, header, sig, ct } = data as Record<string, unknown>;
  if (type !== ENVELOPE_MESSAGE_TYPE) return null;
  if (typeof header !== 'string' || typeof sig !== 'string') return null;
  if (!(ct instanceof ArrayBuffer)) return null;
  return { header, sig, ct };
}

export function receiveEnvelope(viewer: Window, signal?: AbortSignal): Promise<RawEnvelope> {
  return new Promise<RawEnvelope>((resolve, reject) => {
    const launcher = viewer.opener as Window | null;
    if (!launcher) {
      reject(new NoLauncherError());
      return;
    }
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }

    const launcherSilent = setTimeout(onLauncherSilent, LAUNCHER_ANSWER_TIMEOUT_MS);
    const stopListening = () => {
      clearTimeout(launcherSilent);
      viewer.removeEventListener('message', onMessage);
      signal?.removeEventListener('abort', onAbort);
    };
    function onMessage(event: MessageEvent) {
      if (event.source !== launcher) return;
      const envelope = asRawEnvelope(event.data);
      if (!envelope) return;
      stopListening();
      resolve(envelope);
    }
    function onAbort() {
      stopListening();
      reject((signal as AbortSignal).reason);
    }
    function onLauncherSilent() {
      stopListening();
      reject(new NoLauncherError());
    }

    viewer.addEventListener('message', onMessage);
    signal?.addEventListener('abort', onAbort);
    // A launcher opened from disk has origin "null", and "ready" carries nothing secret.
    try {
      launcher.postMessage(READY_MESSAGE, '*');
    } catch (err) {
      stopListening();
      reject(err);
    }
  });
}
